/**
 * admin-api 统一 fetch client — JWT 自动注入 + envelope 解包；access 过期(401) 隐式续期重试
 *
 * 契约：dev-docs 10-API响应格式
 * - 成功：body.code === 'OK' → 返 data
 * - 失败：body.code !== 'OK' → 抛 AdminApiError（前端按 code 分支）
 * - 鉴权注入 + 401→refresh→重试 + 登出决策统一在 ~/api/session-refresh 的 authedFetch（本文件不再各自 clearSession）
 *
 * MVP 阶段手写 fetch wrapper；M12 改走 OpenAPI 生成 client
 */

import { newRequestId } from '~/lib/request-id';
import {
  AdminApiError,
  type AdminErrorBody,
  AdminRequestAbortedError,
  AdminRequestCanceledError,
  AdminRequestTimeoutError,
  UnexpectedAdminApiResponseError,
} from './errors';
import { DEFAULT_REQUEST_TIMEOUT_MS } from './network-aware-fetch';
import { authedFetch } from './session-refresh';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

/**
 * 单个 query 值：标量（string/number/boolean）或标量数组。
 * 数组值序列化为重复参数（?k=a&k=b），与后端 list 端点的多值过滤契约一致
 * （server 用 c.req.queries('<name>') 取 string[]）；空数组 / undefined 跳过不发。
 */
export type ApiQueryValue = string | number | boolean | undefined | (string | number)[];

export type ApiRequest = {
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  body?: unknown;
  query?: Record<string, ApiQueryValue>;
  /** 临时 verify-2fa token 时显式传入；其他场景从 auth-store 自动取 */
  bearerOverride?: string;
  /** 默认 true；设 false 时 401 可续期 session，但禁止自动重放原请求。 */
  replayOnUnauthorized?: boolean;
  /**
   * 调用方的取消信号（页面卸载 / 用户点「取消」）。与内置超时**取并集**：任一触发即中止。
   * 中止后抛 `AdminRequestCanceledError`，与超时的 `AdminRequestTimeoutError` 分开 —— 两者
   * 运营该做的事不同，且都带 `serverStateUnknown`（非 GET 恒 true）。
   */
  signal?: AbortSignal;
  /** 覆盖默认硬超时（毫秒）。省略 = `DEFAULT_REQUEST_TIMEOUT_MS`。 */
  timeoutMs?: number;
  /**
   * 额外请求头（如 Stripe Checkout 配置 PUT 的 `Idempotency-Key`）。
   * 不能覆盖鉴权与追踪头——`Authorization` 由 authedFetch 注入、`X-Request-Id` 每请求生成，
   * 覆盖它们会破坏 session 续期与日志对账，故合并时以内置头为准。
   */
  headers?: Record<string, string>;
};

/** cursor 分页响应：裸数组 data + envelope 顶层 nextCursor / hasMore（okPaged 形态，dev-docs 10-API响应格式 §1.4）*/
export type PagedResult<T> = {
  data: T[];
  nextCursor: string | null;
  hasMore: boolean;
};

/** offset 分页结果（admin books 列表）：data + 真实 total + 当前 page / limit + hasMore */
export type OffsetPagedResult<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
};

/**
 * 解包 envelope：成功返回完整 body（含可选 nextCursor / hasMore / total / page / limit）；失败抛 AdminApiError。
 * apiRequest（只要 data）/ apiRequestPaged（cursor）/ apiRequestOffsetPaged（offset）共用，避免多份 fetch / 401 处理漂移。
 */
async function fetchEnvelope(req: ApiRequest): Promise<{
  data?: unknown;
  message?: string;
  nextCursor?: string | null;
  hasMore?: boolean;
  total?: number;
  page?: number;
  limit?: number;
}> {
  const url = new URL((API_BASE || '') + req.path, window.location.origin);
  if (req.query) {
    for (const [k, v] of Object.entries(req.query)) {
      if (v === undefined) continue;
      // 数组值 → 重复参数（?k=a&k=b，append 而非 set），后端用 c.req.queries(k) 取 string[]；
      // 空数组跳过（不发该 key）。标量直接 set。
      if (Array.isArray(v)) {
        for (const item of v) url.searchParams.append(k, String(item));
      } else {
        url.searchParams.set(k, String(v));
      }
    }
  }

  const headers: Record<string, string> = {
    // 调用方自定义头先铺底，内置头后写覆盖：Authorization / X-Request-Id 等不可被调用方顶掉
    ...req.headers,
    'Content-Type': 'application/json',
    'X-Request-Id': newRequestId(),
  };

  // 鉴权注入 + access 过期(401) 自动 refresh+重试一次 统一交给 authedFetch；临时 token
  // （bearerOverride）经 opts 透传、不触发刷新（见 session-refresh）。续期后若重试成功则返回
  // 200，下方 401 分支不触发；仅当 refresh 也失败、最终仍 401 时才走 clearSession 登出。
  // 超时与调用方取消取并集：内部 controller 既被 timer 触发、也被调用方 signal 透传触发。
  // 用自己的 controller 而不是把 req.signal 直接交给 fetch —— 否则无从区分两种中止来源。
  const timeoutMs = req.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const onCallerAbort = () => controller.abort();
  req.signal?.addEventListener('abort', onCallerAbort, { once: true });
  // 调用方 signal 在发起前就已 abort 时不会再触发事件 —— 显式补一次
  if (req.signal?.aborted) controller.abort();

  let res: Response;
  let bodyJson: unknown;
  try {
    res = await authedFetch(
      url.toString().replace(window.location.origin, ''),
      {
        method: req.method,
        headers,
        body: req.body !== undefined ? JSON.stringify(req.body) : undefined,
        signal: controller.signal,
      },
      {
        bearerOverride: req.bearerOverride,
        replayOnUnauthorized: req.replayOnUnauthorized,
        timeoutMs,
      },
    );
    // json() 必须留在超时窗内：headers 先到、body 卡住时，提前 clearTimeout 会让半开连接再次锁死页面。
    try {
      bodyJson = await res.json();
    } catch (err) {
      // 中止类错误（超时 / 通知条取消）**必须原样上抛**：它们带 `serverStateUnknown`，
      // 吞成 null 会让写请求被报成 INTERNAL_ERROR —— 而那笔写此刻可能已落库。
      // 判 `instanceof` 而非 `name === 'AbortError'`：body 读取被中止时，authedFetch 的
      // Proxy 已把它转成 AdminRequestCanceledError，name 不再是 'AbortError'。
      if (err instanceof AdminRequestAbortedError) throw err;
      if ((err as { name?: string } | null)?.name === 'AbortError') throw err;
      bodyJson = null;
    }
  } catch (err) {
    // network-aware-fetch 对 AbortError 原样上抛（它不是「连不上」）；在这里分流成两个具体类型
    if ((err as { name?: string } | null)?.name === 'AbortError') {
      throw timedOut
        ? new AdminRequestTimeoutError(req.method, timeoutMs)
        : new AdminRequestCanceledError(req.method);
    }
    throw err;
  } finally {
    clearTimeout(timer);
    req.signal?.removeEventListener('abort', onCallerAbort);
  }

  const body = bodyJson as {
    code?: string;
    data?: unknown;
    message?: string;
    nextCursor?: string | null;
    hasMore?: boolean;
    // offset 分页（admin books 列表）顶层字段
    total?: number;
    page?: number;
    limit?: number;
  } & AdminErrorBody;
  if (!body || typeof body !== 'object' || !('code' in body)) {
    throw new UnexpectedAdminApiResponseError(res.status);
  }
  if (body.code !== 'OK') {
    // 登出决策由 authedFetch 统一拥有（仅 refresh 显式失效才 forceLogout；瞬时故障/已登出不在此清）：
    // 此处只抛错、不再 clearSession——避免「瞬时 503 续期失败」或「已 forceLogout」时重复/误清。
    throw new AdminApiError(res.status, body);
  }
  return body;
}

export async function apiRequest<T>(req: ApiRequest): Promise<T> {
  const body = await fetchEnvelope(req);
  return body.data as T;
}

/**
 * 同 apiRequest，另返回成功响应壳的 `message`。仅用于契约明确要求后台展示成功 message 的端点
 * （如 A8 §9.4 Stripe 连接测试的证明读取未就绪提示）；分支判断仍按 data 字段，不解析 message。
 */
export async function apiRequestWithMessage<T>(
  req: ApiRequest,
): Promise<{ data: T; message: string }> {
  const body = await fetchEnvelope(req);
  return { data: body.data as T, message: body.message ?? '' };
}

/**
 * cursor 分页请求：返回裸数组 data + envelope 顶层 nextCursor / hasMore。
 * 用于需翻页累积 / 「加载更多」的列表（如 feedback ADM-FBK-S001），避免 apiRequest 丢弃顶层游标后
 * 只能见首页 → 靠后记录静默消失（adversarial F7）。
 */
export async function apiRequestPaged<T>(req: ApiRequest): Promise<PagedResult<T>> {
  const body = await fetchEnvelope(req);
  const nextCursor = body.nextCursor ?? null;
  return {
    data: (body.data ?? []) as T[],
    nextCursor,
    hasMore: body.hasMore ?? nextCursor !== null,
  };
}

/**
 * offset 分页请求：返回裸数组 data + envelope 顶层 total / page / limit / hasMore。
 * 用于需「真实总数 + 跳任意页」的 admin 列表（如 books ADM-BOK-S003）。total 缺省兜底为 data.length，
 * page/limit 缺省回退请求值（防后端未回显时分页器算错）。
 */
export async function apiRequestOffsetPaged<T>(req: ApiRequest): Promise<OffsetPagedResult<T>> {
  const body = await fetchEnvelope(req);
  const data = (body.data ?? []) as T[];
  const reqPage = Number(req.query?.page ?? 1);
  const reqLimit = Number(req.query?.limit ?? data.length);
  const total = body.total ?? data.length;
  const page = body.page ?? reqPage;
  const limit = body.limit ?? reqLimit;
  const hasOffsetPaginationMetadata =
    body.total !== undefined ||
    body.page !== undefined ||
    body.limit !== undefined ||
    body.hasMore !== undefined;
  return {
    data,
    total,
    page,
    limit,
    // 单向 expand/contract 发布顺序：先发布具备此兼容逻辑的 Web，等待旧 Pages bundle 窗口结束，
    // 再发布始终返回 canonical offset metadata 的 Server。旧 Server 的全量数组必须视为单页，
    // 否则数据量超过请求 limit 时，新 Web 会重复拉取同一全量数组。
    hasMore: body.hasMore ?? (hasOffsetPaginationMetadata && page * limit < total),
  };
}
